<template>
  <div>
    <b-navbar id="header" type="light" variant="light" sticky>
      <b-navbar-nav>
        <h4 class="mb-0">{{title}}</h4>
      </b-navbar-nav>

      <b-navbar-nav class="ml-auto">
        <b-row>
          <b-col>
            <ExportSidebar
              sidebar-id="sidebar-workers-export"
              storage-key="sidebar_workers_export"
              :columns="workersExportColumns"
              :data-to-export="items"
              :data-mapper="mapWorkerForExport"
              file-name="Workers.xlsx"
              sheet-name="Workers"
              :is-busy="isBusy"
              :items-count="totalRows"
            />
          </b-col>
          <b-col cols="auto">
            <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields>
              <font-awesome-icon icon="cog"/>
            </b-button>
            <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
              Колонки
            </b-tooltip>
            <b-button v-if="canEditWorkers()" size="sm" variant="success" :disabled="isBusy" @click="onCreate()">
              <b-spinner v-if="isBusy" small/>
              <font-awesome-icon icon="plus" class="mr-1"/>
              {{ $t("message.workers.importHand") }}
            </b-button>
          </b-col>
        </b-row>
      </b-navbar-nav>
    </b-navbar>

    <filters-drawer
      :query.sync="filters.query"
      :is-busy="isBusy"
      :filter-tags="activeFilterTags"
      @remove-tag="removeFilterTag"
      @submit="onSearch"
      @clear="onClearFilters"
    >
      <b-col md="6" cols="12">
        <label class="small text-muted">Города</label>
        <b-form-select
          id="input-town"
          size="sm"
          v-model="filters.town"
        >
          <option :value="-1">Все города</option>
          <option v-for="option in towns" :value="option.id">
            {{ option.name }}
          </option>
        </b-form-select>
      </b-col>
      <b-col md="6" cols="12">
        <label class="small text-muted">Уровень КПД</label>
        <b-form-select
          id="input-level-efficiency"
          size="sm"
          v-model="filters.level_efficiency"
          :options="levelEfficiencyOptions"
        />
      </b-col>
      <b-col md="6" cols="12">
        <label class="small text-muted">Активные</label>
        <b-form-select
          id="input-active"
          size="sm"
          v-model="filters.active"
        >
          <option :value="null">Показывать все</option>
          <option :value="true">Активные</option>
          <option :value="false">Удалённые</option>
        </b-form-select>
      </b-col>
      <b-col md="6" cols="12">
        <label class="small text-muted">Статус</label>
        <b-form-select
          id="input-status"
          size="sm"
          v-model="filters.status_id"
        >
          <option :value="null">Все статусы</option>
          <option v-for="option in workerStatuses" :value="option.id">
            {{ option.name }}
          </option>
        </b-form-select>
      </b-col>
    </filters-drawer>

    <b-table
      id="workers"
      ref="workers"
      class="mb-0 text-small"
      selectable
      select-mode="single"
      selectedVariant="success"
      striped
      small
      responsive
      show-empty
      :empty-text="$t('message.common.emptyTable')"
      :busy="isBusy"
      :items="items"
      :fields="renderedFields"
      no-provider-filtering
      no-provider-paging
      no-provider-sorting
      no-local-sorting
      @sort-changed="sortChanged"
      @row-dblclicked="onRowClicked"
      sticky-header
      head-variant="light"
    >
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Загрузка</strong>
        </div>
      </template>

      <template #cell(action)="row">
        <b-dropdown size="sm" no-caret>
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v"/>
          </template>
          <b-dropdown-item-button variant="success" @click.stop="onView(row.item)" target="_blank">
            <font-awesome-icon icon="eye"/>
            Смотреть
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditWorkers()" variant="primary" @click="onEdit(row.item)">
            <font-awesome-icon icon="edit"/>
            Редактировать
          </b-dropdown-item-button>
          <b-dropdown-item-button variant="danger" @click="onRemove(row)">
            <font-awesome-icon icon="trash"/>
            Удалить
          </b-dropdown-item-button>
        </b-dropdown>
      </template>

      <template #cell(sex)="row">
        {{ typeof row.item.sex === 'boolean' ? (row.item.sex ? 'М' : 'Ж') : '' }}
      </template>

      <template #cell(level_efficiency)="row">
        <b-badge :id="'level-efficiency-badge-' + row.item.id" pill :variant="getLevelEfficiencyStyle(row.item.level_efficiency)">
          {{ getLevelEfficiencyText(row.item.level_efficiency)}}
        </b-badge>
        <b-tooltip
          v-if="row.item.description !== ''"
          :target="'level-efficiency-badge-' + row.item.id"
          triggers="hover"
          :variant="getLevelEfficiencyStyle(row.item.level_efficiency)"
          noninteractive
        >
          {{ row.item.description }}
        </b-tooltip>
      </template>

      <template #cell(employment_type)="row">
        {{ row.item.employment_type === 2 ? 'Да' : 'Нет' }}
      </template>

      <template #cell(phone)="row">
        <a :href=telUrl(row.item.phone)>{{phoneString(row.item.phone)}}</a>
      </template>

      <template #cell(passport_url)="row">
        <a
          v-if="row.item.passport_url.length > 0"
          @click="downloadFile(row.item.id, row.item.passport_url)"
        >
          <font-awesome-icon  icon="passport" size="lg" :style="{ color: 'purple' }"/>
        </a>
      </template>

      <template #cell(active)="row">
        <font-awesome-icon v-if="!row.item.active" icon="times"/>
        <font-awesome-icon v-if="row.item.active" icon="check"/>
      </template>

      <template #cell(status_id)="row">
        {{ row.item.status_name }}
      </template>
    </b-table>

    <pagination-footer
      :total-rows="totalRows"
      :per-page.sync="perPage"
      @page-click="onPagination"
      @per-page-change="onPerPageChange"
    />

    <DynamicTables
      storage-key="workers_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      :serviceEndField="serviceEndField"
      fieldKey="id"
    />

  </div>
</template>

<script>
import workers from "../../network/workers";
import catalogs from "../../network/catalogs";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";

export default {
  name: "WorkerTable",
  components: { PaginationFooter, ExportSidebar, FiltersDrawer, DynamicTables },
  mixins: [
    workers, catalogs,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Исполнители',
      formUrl: 'workers',
      isBusy: false,
      items: [],
      fields: [
        {
          key: 'id',
          label: 'id',
          class: 'workers-id',
          sortable: true,
          visible: true
        },
        {
          key: 'name',
          label: this.$t('message.workers.table.name'),
          sortable: true,
          visible: true
        },
        {
          key: 'phone',
          label: this.$t('message.workers.table.phone'),
          sortable: true,
          visible: true
        },
        {
          key: 'address',
          label: this.$t('message.workers.table.address'),
          sortable: true,
          visible: true
        },
        {
          key: 'passport_url',
          label: this.$t('message.workers.table.passport'),
          sortable: true,
          formatter:'existPassport',
          class: 'workers-passport',
          visible: true
        },
        {
          key: 'sex',
          label: 'Пол',
          sortable: true,
          visible: true
        },
        {
          key: 'passport_type',
          label: 'Гражданство',
          sortable: true,
          visible: true
        },
        {
          key: 'age',
          label: this.$t('message.workers.table.age'),
          sortable: true,
          visible: true
        },
        {
          key: 'skill',
          label: this.$t('message.workers.table.skill'),
          sortable: true,
          visible: true
        },
        {
          key: 'ready',
          label: this.$t('message.workers.table.ready'),
          sortable: true,
          visible: true
        },
        {
          key: 'level_efficiency',
          label: 'Ур.КПД',
          sortable: true,
          visible: true
        },
        {
          key: 'employment_type',
          label: "СМЗ",
          sortable: true,
          visible: true,
          class: 'text-center'
        },
        {
          key: 'active',
          label: "Активный",
          sortable: true,
          visible: true,
          class: 'text-center'
        },
        {
          key: 'status_id',
          label: "Статус",
          sortable: true,
          visible: true,
          class: 'text-center'
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.workers.table.actions'),
        class: 'workers-action'
      },
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      towns: [],
      workerStatuses: [],
      deleteRow: {
        id: null,
        name: '',
      },
      levelEfficiencyOptions: [
        { value: '', text: 'Все уровни' },
        { value: 'high', text: 'Высокий' },
        { value: 'medium', text: 'Средний' },
        { value: 'low', text: 'Низкий' },
      ],
      workersExportColumns: {
        "id": true,
        "Имя": true,
        "Телефон": true,
        "Адрес": true,
        "Пол": true,
        "Гражданство": true,
        "Возраст": true,
        "Умение": true,
        "Готовность": true,
        "Ур. КПД": true,
        "СМЗ": true,
        "Активный": true,
        "Статус": true,
      },
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.town) {
        this.filters.town = query.town;
      }

      if (query.active) {
        this.filters.active = query.active === 'all' ? null : query.active;
      }

      if (query.level_efficiency) {
        this.filters.level_efficiency = query.level_efficiency;
      }

      if (query.status_id) {
        this.filters.status_id = query.status_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.towns = (await this.getTowns({})).data.items;
    this.workerStatuses = (await this.getWorkerStatuses({})).data.items;
    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.town !== defaults.town) {
        const foundItem = this.towns.find(s => s.id === this.filters.town);
        tags.push({ key: 'town', label: foundItem ? foundItem.name : 'Город' });
      }

      if (this.filters.active !== defaults.active) {
        tags.push({ key: 'active', label: 'Активные' });
      }

      if (this.filters.level_efficiency !== defaults.level_efficiency) {
        const foundItem = this.levelEfficiencyOptions.find(s => s.value === this.filters.level_efficiency);
        tags.push({ key: 'level_efficiency', label: foundItem ? foundItem.text : 'Уровень КПД' });
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.workerStatuses.find(s => s.value === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус' });
      }

      return tags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        town: this.filters.town,
        active: this.filters.active,
        status_id: this.filters.status_id,
        level_efficiency: this.filters.level_efficiency,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getWorkers(data);

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.isBusy = false;
    },
    onRowClicked(item) {
      if (this.hasPermission([PERM.WORKER_EDIT])) {
        this.onEdit(item);
        return;
      }

      if (this.hasPermission([PERM.WORKER_READ])) {
        this.onView(item);
        return;
      }
    },
    onEdit(item) {
      this.$router.push({ name: 'WorkersEdit', params: { worker_id: item.id, isRead: false }}).catch(()=>{});
    },
    onView(item) {
      this.$router.push({ name: 'WorkersView', params: { worker_id: item.id, isRead: true }}).catch(()=>{});
    },
    onCreate() {
      this.$router.push({ path: 'workers/new'}).catch(()=>{});
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = this.$t('message.workers.modal.removeTitle');
      const message = `${this.$t("message.workers.modal.removeDesc")} ${this.deleteRow.name}?`;
      this.showModalDelete(title, message, async () => {

        const result = await this.deleteWorker(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заказчик удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    getLevelEfficiencyText(value) {
      const option = this.levelEfficiencyOptions.find(opt => opt.value === value);
      return option.text;
    },
    getLevelEfficiencyStyle(value) {
      switch (value) {
        case 'high':
          return 'success';
        case 'medium':
          return 'warning';
        case 'low':
          return 'danger';
        default:
          return '';
      }
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        town: -1,
        active: true,
        status_id: null,
        level_efficiency: '',
      }
    },
    removeFilterTag(tag) {
      if (tag.key === 'town') {
        this.filters.town = -1;
      }
      else if (tag.key === 'active') {
        this.filters.active = true;
      }
      else if (tag.key === 'status') {
        this.filters.status_id = null;
      }
      else if (tag.key === 'level_efficiency') {
        this.filters.level_efficiency = '';
      }
      this.onSearch();
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();
      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }
      this.filters = filters;
      this.onSearch();
    },
    // Export
    async downloadFile(id, passportUrl) {
      const fileName = passportUrl.split("/").pop();
      const result = await this.getWorkerFile(id);

      if (result.error || !result.data) {
        this.showError("Ошибка загрузки файла", result.error.message);
        return;
      }

      this.handleFileDownload(result.data, fileName);
    },
    existPassport(value) {
      return value.length > 0;
    },
    mapWorkerForExport(item, selectedColumns) {
      const config = {
        "id": item.id,
        "Имя": item.name,
        "Телефон": item.phone,
        "Адрес": item.address,
        "Пол": item.sex === true ? 'Мужчина' : 'Женщина',
        "Гражданство": item.passport_type,
        "Возраст": item.age,
        "Умение": item.skill,
        "Готовность": item.ready,
        "Ур. КПД": this.getLevelEfficiencyText(item.level_efficiency),
        "СМЗ": item.employment_type === 2 ? 'Да' : 'Нет',
        "Статус": item.status_name
      };
      const excelData = {};

      for (const key in config) if (selectedColumns[key]) {
        excelData[key] = config[key];
      }
      return excelData;
    },
    // Permissions
    canEditWorkers() {
      return this.hasPermission([PERM.WORKER_EDIT]);
    }
  },
}
</script>

<style>
@media screen
and (min-width: 768px) {
  a[href*="tel:"] {
    pointer-events: none;
    cursor: default;
    text-decoration: none;
  }
}

#workers td {
  vertical-align: middle;
}

.workers-id {
  padding-left: 10px !important;
  width: 60px;
  text-align: center;
}

.workers-passport {
  width: 80px;
  text-align: center;
}

.workers-action {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Workers/WorkerTable.vue